/**
 * useRegulationsData Hook
 * 处理法规数据的加载逻辑
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import type { Dayjs } from "dayjs";
import type {
  GridFilterModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import {
  searchRegulations,
  fetchRegulationFavorites,
  transformToTableRow,
  regulationsSortFromGridSortModel,
  regulationsConditionsFromGridFilterModel,
  gridFilterConditionsCacheKey,
  type FieldConfig,
  type RegulationRow,
  type ConceptValue,
} from "../../../services/regulationsService";
import { buildListGridFilterApiFieldMap } from "./listTableColumnConfig";

interface UseRegulationsDataParams {
  paginationModel: {
    page: number;
    pageSize: number;
  };
  /** Server-side sort from Data Grid (mapped to API inside searchRegulations payload) */
  sortModel?: GridSortModel;
  /** Column filter panel → `filters.conditions` */
  filterModel?: GridFilterModel;
  /** List fields config — used to map dynamic auto-tag columns to filter_field_name */
  fieldsConfig?: FieldConfig[];
  selectedMarkets: ConceptValue[];
  selectedProductTypes: ConceptValue[];
  selectedHA: ConceptValue[];
  selectedTags: ConceptValue[];
  selectedTitle: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  /** Favorite dimension. Sent as a search condition, same shape as the other filters. */
  favoriteFilter?: "favorited" | "not_favorited" | null;
  domain: string;
  /**
   * When false, skip fetching until the caller has resolved an allowed domain
   * (avoids querying default MPR before view_* privileges load).
   */
  enabled?: boolean;
  /** When true (Show Favorite preset), list loads via GET /regulations/favorites only */
  favoritesListMode?: boolean;
}

/** Guard against invalid grid/store pagination (e.g. NaN serializes as null in JSON). */
function coercePaginationModel(model: UseRegulationsDataParams["paginationModel"]) {
  const p = Number(model.page);
  const s = Number(model.pageSize);
  return {
    page: Number.isFinite(p) && p >= 0 ? Math.trunc(p) : 0,
    pageSize: Number.isFinite(s) && s > 0 ? Math.trunc(s) : 10,
  };
}

export const useRegulationsData = ({
  paginationModel,
  sortModel = [],
  filterModel = { items: [] },
  fieldsConfig = [],
  selectedMarkets,
  selectedProductTypes,
  selectedHA,
  selectedTags,
  selectedTitle,
  fromDate,
  toDate,
  favoriteFilter = null,
  domain,
  enabled = true,
  favoritesListMode = false,
}: UseRegulationsDataParams) => {
  const [rows, setRows] = useState<RegulationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const gridFilterApiFieldMap = useMemo(
    () => buildListGridFilterApiFieldMap(fieldsConfig),
    [fieldsConfig],
  );
  const gridColumnFilterConditionsKey = gridFilterConditionsCacheKey(
    filterModel,
    gridFilterApiFieldMap,
  );

  const loadData = useCallback(async () => {
    if (!enabled) return;
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);

      const pm = coercePaginationModel(paginationModel);

      if (favoritesListMode) {
        const response = await fetchRegulationFavorites({
          page: pm.page + 1,
          page_size: pm.pageSize,
        });
        if (requestId !== requestIdRef.current) return;

        if (response.code === 200 && response.data) {
          const tableRows = response.data.items.map(transformToTableRow);
          setRows(tableRows);
          setTotalRows(response.data.total);
          setLoadError(null);
        } else {
          console.error("Failed to load favorites:", response.message);
          setLoadError(response.message || "Failed to load favorites");
          // Keep totalRows so server pagination does not snap back to page 0.
        }
        return;
      }

      // Build filter conditions
      const conditions: Array<{
        field: string;
        operator: string;
        value: string | number | boolean | string[];
      }> = [];

      // Add market filter if selected (multi-select: single condition with value as array)
      if (selectedMarkets && selectedMarkets.length > 0) {
        conditions.push({
          field: "markets",
          operator: "eq",
          value: selectedMarkets.map((m) => m.name),
        });
      }

      // Add product types filter if selected
      if (selectedProductTypes && selectedProductTypes.length > 0) {
        conditions.push({
          field: "product_types",
          operator: "eq",
          value: selectedProductTypes.map((pt) => pt.name),
        });
      }

      // Add HA filter if selected
      if (selectedHA && selectedHA.length > 0) {
        conditions.push({
          field: "ha",
          operator: "eq",
          value: selectedHA.map((h) => h.name),
        });
      }

      // Add tags filter if selected
      if (selectedTags && selectedTags.length > 0) {
        conditions.push({
          field: "tags",
          operator: "eq",
          value: selectedTags.map((t) => t.name),
        });
      }

      // Add published date filters (Published Date Range)
      if (fromDate) {
        conditions.push({
          field: "published_date",
          operator: "gte",
          value: fromDate.format("YYYY-MM-DD"),
        });
      }
      if (toDate) {
        conditions.push({
          field: "published_date",
          operator: "lte",
          value: toDate.format("YYYY-MM-DD"),
        });
      }

      if (favoriteFilter === "favorited" || favoriteFilter === "not_favorited") {
        conditions.push({
          field: "favorite",
          operator: "eq",
          value: favoriteFilter,
        });
      }

      conditions.push(
        ...regulationsConditionsFromGridFilterModel(
          filterModel,
          gridFilterApiFieldMap,
        ),
      );

      const sort = regulationsSortFromGridSortModel(sortModel);

      const requestParams = {
        query: selectedTitle ? selectedTitle.trim() : "",
        pagination: {
          page: pm.page + 1, // API page numbers start from 1
          page_size: pm.pageSize,
        },
        filters: {
          status: "3",
          conditions,
        },
        fields: [], // Empty array means return all fields
        ...(sort != null ? { sort } : {}),
      };

      const response = await searchRegulations(requestParams);
      if (requestId !== requestIdRef.current) return;

      if (response.code === 200 && response.data) {
        // Transform backend data to table row data
        const tableRows = response.data.items.map(transformToTableRow);
        setRows(tableRows);
        setTotalRows(response.data.total);
        setLoadError(null);
      } else {
        console.error("Failed to load regulations:", response.message);
        setLoadError(response.message || "Failed to load regulations");
        setRows([]);
        // Preserve totalRows — clearing it resets controlled pagination to page 0.
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error loading regulations:", error);
      const message =
        error instanceof Error ? error.message : "Failed to load regulations";
      setLoadError(message);
      setRows([]);
      // Preserve totalRows on transport/API errors (e.g. 413 Content Too Large).
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    selectedMarkets,
    selectedProductTypes,
    selectedHA,
    selectedTags,
    selectedTitle,
    fromDate,
    toDate,
    favoriteFilter,
    domain,
    enabled,
    favoritesListMode,
    sortModel,
    gridColumnFilterConditionsKey,
    gridFilterApiFieldMap,
  ]);

  const updateRowFavorite = useCallback(
    (regulationId: string, isFavorite: boolean) => {
      setRows((prev) =>
        prev.map((r) =>
          r.id === regulationId ? { ...r, isFavorite } : r,
        ),
      );
    },
    [],
  );

  // Load data on initialization and when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  return { rows, loading, totalRows, loadError, loadData, updateRowFavorite };
};
